/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useEffect, useContext } from 'react';
import { Collapse } from 'reactstrap';
import moment from 'moment';
import Alert from 'react-s-alert';
import { Link, Prompt } from 'react-router-dom';

// context imports
import { ThemeContext } from '../../../contexts/ThemeContext'

// service imports
import Message from '../../general/Message';
import ProviderService from '../../../services/ProviderService';
import GeneralService from '../../../services/GeneralService';

import { counsellors } from '../demoData';

const AvailableTimings = props => {

    // contexts
    const { theme } = useContext(ThemeContext);

    // states
    const timeRange = ["12:00 AM", "12:30 AM", "01:00 AM", "01:30 AM", "02:00 AM", "02:30 AM", "03:00 AM", "03:30 AM", "04:00 AM", "04:30 AM", "05:00 AM", "05:30 AM", "06:00 AM", "06:30 AM", "07:00 AM", "07:30 AM", "08:00 AM", "08:30 AM", "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM", "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM", "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM", "05:00 PM", "05:30 PM", "06:00 PM", "06:30 PM", "07:00 PM", "07:30 PM", "08:00 PM", "08:30 PM", "09:00 PM", "09:30 PM", "10:00 PM", "10:30 PM", "11:00 PM", "11:30 PM", "12:00 AM"];
    const [timeRanges, setTimeRanges] = useState([["12:00 AM", "12:30 AM", "01:00 AM", "01:30 AM", "02:00 AM", "02:30 AM", "03:00 AM", "03:30 AM", "04:00 AM", "04:30 AM", "05:00 AM", "05:30 AM", "06:00 AM", "06:30 AM", "07:00 AM", "07:30 AM", "08:00 AM", "08:30 AM", "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM", "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM", "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM", "05:00 PM", "05:30 PM", "06:00 PM", "06:30 PM", "07:00 PM", "07:30 PM", "08:00 PM", "08:30 PM", "09:00 PM", "09:30 PM", "10:00 PM", "10:30 PM", "11:00 PM", "11:30 PM", "12:00 AM"]]);
    const [isAvailableTimingsOpen, setIsAvailableTimingsOpen] = useState(true);
    const [isDataModified, setIsDataModified] = useState(false);
    const [oldAvailableTimings, setOldAvailableTimings] = useState([]);
    const [availableTimings, setAvailableTimings] = useState([]);
    const [profileComplete, setProfileComplete] = useState({
        status: true,
        message: {
            msgBody: [`Looks like your profile is incomplete! Please complete that by going to `, <b key="profileLink"><Link to='/counsellor/profile'>Profile</Link></b>, ` page to enjoy our service. Unless your profile is completed you won't be visible to any clients.`],
            msgError: true
        }
    });
    const [isLoading, setIsLoading] = useState(false);

    // useEffects
    useEffect(() => {
        setOldAvailableTimings(counsellors[0].availableTimes);
        setAvailableTimings(counsellors[0].availableTimes);
        return () => {
            setIsDataModified(false);
            window.onbeforeunload = undefined
        }
    }, []);

    useEffect(() => {
        if (isDataModified) {
            window.onbeforeunload = () => true
        } else {
            window.onbeforeunload = undefined
        }
    }, [isDataModified]);

    // utility functions
    const toggleAvailableTimings = () => {
        setIsAvailableTimingsOpen(!isAvailableTimingsOpen);
    }

    const handleTimingsChange = (type, index, value) => {
        try {
            let tempArray = [...availableTimings];
            if (type === "update") {
                if (value.name === "from") {
                    tempArray[index].from = moment(value.value, "hh:mm A").utcOffset(props.selectedTimeZone.isDST ? props.selectedTimeZone.dst_offset : props.selectedTimeZone.offset).toISOString();
                } else if (value.name === "to") {
                    tempArray[index].to = moment(value.value, "hh:mm A").utcOffset(props.selectedTimeZone.isDST ? props.selectedTimeZone.dst_offset : props.selectedTimeZone.offset).toISOString();
                }
            } else {
                if (type === "add") {
                    if (availableTimings.length) {
                        const lastToIndex = timeRange.indexOf(moment(availableTimings[index].to).utcOffset(props.selectedTimeZone.isDST ? props.selectedTimeZone.dst_offset : props.selectedTimeZone.offset).format("hh:mm A"));
                        tempArray.push({ from: moment(timeRange[lastToIndex + 1], "hh:mm A").utcOffset(props.selectedTimeZone.isDST ? props.selectedTimeZone.dst_offset : props.selectedTimeZone.offset).toISOString(), to: moment(timeRange[lastToIndex + 2], "hh:mm A").utcOffset(props.selectedTimeZone.isDST ? props.selectedTimeZone.dst_offset : props.selectedTimeZone.offset).toISOString() });
                    } else {
                        tempArray.push({ from: moment("10:00 AM", "hh:mm A").utcOffset(props.selectedTimeZone.isDST ? props.selectedTimeZone.dst_offset : props.selectedTimeZone.offset).toISOString(), to: moment("02:00 PM", "hh:mm A").utcOffset(props.selectedTimeZone.isDST ? props.selectedTimeZone.dst_offset : props.selectedTimeZone.offset).toISOString() });
                    }
                } else if (type === "remove") {
                    tempArray.splice(index, 1);
                }
            }
            setIsDataModified(true);
            setAvailableTimings(tempArray);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Provider AvailableTimings handleTimingsChange', 'Error in try catch block', err.stack);
        }
    }

    const handleSubmit = () => {
        setIsLoading(true);
        setTimeout(() => {
            Alert.info("Available timings updated successfully", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsDataModified(false);
            props.callAvailableDaysRefresh(availableTimings);
            setIsLoading(false);
        })
    }

    return (
        <div className="p-3 mt-3" style={theme.card} id="availableTimings">
            <Prompt
                when={isDataModified}
                message='You have unsaved changes, are you sure you want to leave?'
            />
            <div id="click-change-timing" className="row px-3" onClick={toggleAvailableTimings} style={{ cursor: "pointer" }}>
                <span id="change-available-timing" style={theme.headingBold}>Change Available Timing</span>
                <span className="ml-auto" style={theme.headingRegular}>
                    {
                        isAvailableTimingsOpen
                            ? <span className="ml-3"><i className="fas fa-angle-up"></i></span>
                            : <span className="ml-3"><i className="fas fa-angle-down"></i></span>
                    }
                </span>
            </div>

            {profileComplete.status ? null : <Message message={profileComplete.message} />}

            <Collapse isOpen={isAvailableTimingsOpen}>
                <hr />
                <div>
                    {
                        availableTimings.map((item, index) => (
                            <div key={index}>
                                <div className="row">

                                    <div className="mt-2 col-md-5 col-12">
                                        <label id="available-timing-from" htmlFor={`from${index}`} style={Object.assign({ width: "122px" }, theme.subHeadingSemibold)}>Available from</label>
                                        <select className="ml-3 px-3 py-2 text-center" id={`from${index}`} name="from" value={moment(item.from).utcOffset(props.selectedTimeZone.isDST ? props.selectedTimeZone.dst_offset : props.selectedTimeZone.offset).format("hh:mm A")} onChange={(e) => handleTimingsChange("update", index, { name: e.target.name, value: e.target.value })} style={theme.card}>
                                            {
                                                timeRange.map((item, index2) => (
                                                    <option key={index2} value={item}>{item}</option>
                                                ))
                                            }
                                        </select>
                                    </div>

                                    <div className="mt-2 col-md-5 col-12">
                                        <label id="available-timing-to" htmlFor={`to${index}`} style={Object.assign({ width: "122px" }, theme.subHeadingSemibold)}>Available to</label>
                                        <select className="ml-3 px-3 py-2 text-center" id={`to${index}`} name="to" value={moment(item.to).utcOffset(props.selectedTimeZone.isDST ? props.selectedTimeZone.dst_offset : props.selectedTimeZone.offset).utcOffset(props.selectedTimeZone.isDST ? props.selectedTimeZone.dst_offset : props.selectedTimeZone.offset).format("hh:mm A")} onChange={(e) => handleTimingsChange("update", index, { name: e.target.name, value: e.target.value })} style={theme.card}>
                                            {
                                                timeRange.slice((timeRange.indexOf(moment(availableTimings[index].from).utcOffset(props.selectedTimeZone.isDST ? props.selectedTimeZone.dst_offset : props.selectedTimeZone.offset).format("hh:mm A")) + 1), timeRange.length).map((item, index2) => (
                                                    <option key={index2} value={item}>{item}</option>
                                                ))
                                            }
                                        </select>
                                    </div>

                                    {
                                        index > 0 ?
                                            <div className="mt-2 col-md-2 col-12">
                                                <button className="btn" onClick={() => handleTimingsChange("remove", index, null)} style={theme.buttonDanger} id="removeBtn">Remove</button>
                                            </div>
                                            : null
                                    }

                                </div>
                                <br />
                            </div>
                        ))
                    }
                    <button className="btn" onClick={() => handleTimingsChange("add", (availableTimings.length - 1), null)} style={theme.button} id="addBtn">Add more</button><br /><br />
                    <button className="btn" onClick={handleSubmit} style={isDataModified ? theme.buttonWarning : theme.button} id="saveBtn" disabled={isLoading}>{isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save</button>
                    <span className="mt-5 px-3 " style={theme.paragraph}>*New timings will reflect from the date when there is no session booked</span>
                </div>
            </Collapse>
        </div >
    )
}

export default AvailableTimings;